import { CURRENCY_SYMBOL } from 'shared';

export interface CurrencyFormatOptions {
  allowDecimal?: boolean;
  allowLeadingZeros?: boolean;
  allowNegative?: boolean;
  useComaForDecimals?: boolean;
}

export interface InputMaskCurrencyOptions extends CurrencyFormatOptions {
  currencySymbol?: string;
  noValueCharacter?: string;
}

const currency = {
  format: (value: string, options: CurrencyFormatOptions) => {
    const isNegative = value.substr(0, 1) === '-' || value === '0-';

    if (options.useComaForDecimals) {
      value = value.toString().replace('.', ',');
    }

    // Only allows numbers without leading zeros
    if (!options.allowLeadingZeros && value !== '0') {
      value = value.replace(/[^0-9,]/g, '').replace(/^0+/, '');
    }

    // Only one comma
    if (value.indexOf(',') > -1) {
      const split = value.split(',');
      if (options.allowDecimal) {
        let afterDecimal = split[1] || '';
        afterDecimal = afterDecimal.substr(0, 2); // We only support 2 decimal places for currency
        value = split[0] + ',' + afterDecimal;
      } else {
        value = split[0];
      }
    }

    // Add the periods back in
    value = addPeriods(value);

    return `${isNegative && options.allowNegative ? '-' : ''}${value}`;
  },
  formatCurrency: (num: number, symbol: string) => {
    const formattedNum = currency.format(num.toString(), { allowDecimal: true });
    // TODO Currency Parse
    return formattedNum ? `${symbol}${formattedNum}` : '-';
  },
  formatLocaleCurrency: (amount: number, currencyCode: string) => {
    return amount.toLocaleString(window.navigator.language, { style: 'currency', currency: currencyCode });
  },
  /**
   * @param {number} amount Value introduced to be parsed into BRL
   */
  formatBrazilCurrency: (amount: number | string) => {
    const amountConverted = currency.format(amount.toString().replace('.', ','), { allowDecimal: true });
    return amountConverted ? `${CURRENCY_SYMBOL.BR}${amountConverted}` : '-';
  },

  /**
   * @param {number} amount - Value introduced to be parsed
   * @param {string} [currencySymbol] - Currency symbol to display and controls whether to show the currency symbol or not
   * @param {string} [noValueCharacter = '-'] - Character to display when amount is provided
   * @param {boolean} [allowDecimals = true] - Display decimals or not
   */
  inputMaskCurrency: (
    amount: number | string | undefined,
    options: InputMaskCurrencyOptions = {
      currencySymbol: CURRENCY_SYMBOL.BR,
      noValueCharacter: '0',
      allowDecimal: true
    }
  ) => {
    const { currencySymbol = '', noValueCharacter = '0', allowDecimal = true, ...formatOptions } = options;

    const amountConverted = currency.format(amount ? amount.toString() : noValueCharacter, {
      allowDecimal,
      ...formatOptions
    });
    return amountConverted ? `${currencySymbol}${amountConverted}` : noValueCharacter;
  },

  formatFromServer: (value: any, allowDecimal: boolean) => {
    value = value.toString().replace('.', ',');
    return currency.format(value, { allowDecimal });
  }
};

const addPeriods = (value: string) => {
  // Separate at the decimal point, we only care about the left hand side
  const split = value.split(',');
  const leftHand = split[0];
  const rightHand = split[1];

  //  Reverse the string first, because separators are added every 3rd character, in reverse
  let newLeftHand = '';
  leftHand
    .split('')
    .reverse()
    .forEach((char, i) => {
      newLeftHand += char;

      // We do not want a separator if there's no number before it (i.e don't do ,888 only 8,888)
      if ((i + 1) % 3 === 0 && leftHand.length > i + 1) {
        newLeftHand += '.';
      }
    });
  // Since we were working with the string backwards, reverse it again
  newLeftHand = newLeftHand
    .split('')
    .reverse()
    .join('');

  // Don't bother adding the right hand side back, if there was no right hand side
  if (rightHand || value.indexOf(',') > -1) {
    return newLeftHand + ',' + rightHand;
  }
  return newLeftHand;
};

export { currency };
